import * as React from 'react'
import { Field, FieldProps, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import TextField from '../../TextField'
import { ICreateDriver, IProviderObject, IDriverSearchObject, IUpdateDriver } from '../../../../types'
import UIMessages from '../../../../utils/ui-messages'
import { isNotBlank, isValidEmail, isValidDriversLicense } from '../../../../utils/validators'
import EditDriverBottomBar from '../../EditDriverBottomBar'
import PreloaderCircle from '../../../ui/PreloaderCircle'
import MaskedPhoneNumberInput from '../../../ui/formik/MaskedPhoneNumberInput'
import ErrorMessage from '../../../ui/ErrorMessage'
import ImageUploadDriver from '../../../ui/ImageUploadDriver'
import * as classNames from 'classnames'
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import moment, { Moment } from 'moment'
import Toggle from 'src/components/ui/Toggle'
import history from 'src/history'
import { FETCH_ALL_ROLES_SUCCESS } from 'src/state/constants'
import { Datepicker, MbscCalendarEvent } from '@mobiscroll/react'
import FloatingForm from 'src/components/ui/form/FloatingForm'
import FormLabel from 'src/components/ui/form/FormLabel'
import { Input, Switch } from '@mobiscroll/react-latest'
import { IDriverAddress } from 'src/services/Drivers'

export interface IFormValues {
  formError?: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  countryCode: string
  username: string
  birthDate: string
  driversLicense?: string
  isAvailableForBackToBack?: boolean
  darkmode?: boolean
  addressId?: string
  street?: string
  city?: string
  state?: string
  zip?: string
  country?: string
}

interface IFormProps {
  driver: IDriverSearchObject
  loading: boolean
}

interface IField {
  onChange: (e: React.ChangeEvent<any>) => void
  onBlur: (e: any) => void
  value: any
  name: string
}

interface IOperatorInfoFormProps {
  handleSubmit: (data: IUpdateDriver) => void
  driver: IDriverSearchObject
  history: any
  match: any
  submitBtnText: string
  loading: boolean
}

export const InnerForm = (props: IFormProps & FormikProps<IFormValues>): any => {
  const { errors, loading, touched, driver, handleSubmit, values, setFieldValue, setFieldTouched, handleBlur } = props

  const [availability, setAvailability] = React.useState(values.isAvailableForBackToBack)
  const [darkmode, setDarkmode] = React.useState(values.darkmode)

  React.useEffect(() => {
    // Ensure the country code is prefixed with '+' only if it doesn't already have it
    const normalizedPhone =
      values.countryCode !== undefined
        ? values.countryCode.startsWith('+')
          ? values.countryCode + values.phone
          : '+' + values.countryCode + values.phone
        : ''

    handlePhoneField(normalizedPhone) // Initialize the phone field
  }, [])

  const textInput = ({ field }: FieldProps<IFormValues>, label: string) => {
    return (
      <Input
        type='text'
        className='mobiscroll-input form'
        {...field}
        label={label}
        theme='ios'
        themeVariant='light'
        inputStyle='box'
        labelStyle='floating'
      />
    )
  }

  const handlePhoneField = (value: any) => {
    setFieldTouched('phone')
    setFieldValue('phone', value)
  }

  const handleToggle = () => {
    setAvailability(!availability)
    setFieldValue('isAvailableForBackToBack', !availability)
  }
  const handleDarkmodeToggle = () => {
    setDarkmode(!darkmode)
    setFieldValue('darkmode', !darkmode)
  }

  const getCompletePhoneNumber = (countryCode: string, phone: string) => {
    const phoneCountryCode = '+' + countryCode

    let phoneNumber = ''
    if (driver.phone ? driver.phone.indexOf(countryCode) === 0 : false) {
      phoneNumber = phone ? phone.replace(countryCode, '') : ''
    } else {
      phoneNumber = phone ? phone : ''
    }

    return phoneNumber.trim() === '' ? '' : (phoneCountryCode + ' ' + phoneNumber).trim()
  }

  const phone = getCompletePhoneNumber(driver.countryCode, driver.phone ? driver.phone : '')

  return (
    <Form>
      <ErrorMessage visible={!!errors.formError} className='mt1'>
        {UIMessages.driver.personalInfo.errorOnUpdate}
      </ErrorMessage>
      <FloatingForm fullHeight={true}>
        <>
          <div className='form-modal-section main-section'>
            <div className='flex flex-row mb1'>
              <div style={{ flex: 1 }}>
                {/* Label from ui-messages */}
                <FormLabel>{UIMessages.common.photo}</FormLabel>
                {/* Logo get from import ImageUpload from '../../../ui/ImageUpload'  */}
                <ImageUploadDriver name={driver.name} entityId={driver.id} />
                {/* Logo get from import ImageUpload from '../../../ui/ImageUpload'  */}
              </div>
            </div>
            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <Field
                  className='FieldFirstName'
                  name='firstName'
                  render={field => textInput(field, `${UIMessages.common.firstName}*`)}
                />
                {touched.firstName && errors.firstName && (
                  <span className='ErrorMsgDivForFirstName font-small weight-300 font-red'>{errors.firstName}</span>
                )}
              </div>
              <div style={{ flex: 1 }} className='block ml1 mt1'>
                <Field name='email' render={field => textInput(field, `${UIMessages.common.email}*`)} />
                {touched.email && errors.email && (
                  <span className='ErrorMsgDivForEmail font-small weight-300 font-red'>{errors.email}</span>
                )}
              </div>
            </div>
            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <Field name='lastName' render={field => textInput(field, `${UIMessages.common.lastName}*`)} />
                {touched.lastName && errors.lastName && (
                  <span className='ErrorMsgDivForLastName font-small weight-300 font-red'>{errors.lastName}</span>
                )}
              </div>
              <div style={{ flex: 1 }} className='ml1 mt1'>
                <PhoneInput
                  className={classNames('phone-input')}
                  name='phone'
                  placeholder='Phone number'
                  defaultCountry='US'
                  value={phone}
                  onChange={handlePhoneField}
                  onBlur={handleBlur}
                />
                {touched.phone && errors.phone && (
                  <span className='font-small weight-300 font-red'>{errors.phone}</span>
                )}
              </div>
            </div>

            <div className='flex flex-row items-end full-width-datepicker mt1'>
              <div style={{ width: '50%' }}>
                <Field name='username' render={field => textInput(field, `${UIMessages.common.username}*`)} />
                {touched.username && errors.username && (
                  <span className='font-small weight-300 font-red'>{errors.username}</span>
                )}
              </div>
              <div style={{ width: '50%' }} className='flex flex-column ml1 h100'>
                <div className='ios-datepicker h100'>
                  <div className='datepicker-label'>{`${UIMessages.common.birthday}*`}</div>
                  <Field name='birthDate'>
                    {({ field, form }: { field: IField; form: FormikProps<any> }) => {
                      const onChange = (date: MbscCalendarEvent) => {
                        form.setFieldValue(field.name, date ? moment(date.value).format('L') : undefined)
                      }
                      return (
                        <div>
                          <Datepicker
                            controls={['calendar']}
                            dateFormat='MM/DD/YYYY'
                            max={moment()}
                            value={form.values[field.name] ? moment(form.values[field.name]) : null}
                            onChange={onChange}
                            theme='ios'
                            themeVariant='light'
                            inputStyle='box'
                            labelStyle='floating'
                            touchUi={false}
                            display='anchored'
                            calendarScroll='horizontal'
                          />
                        </div>
                      )
                    }}
                  </Field>
                  {touched.birthDate && errors.birthDate && (
                    <span className='font-small weight-300 font-red'>{errors.birthDate}</span>
                  )}
                </div>
              </div>
            </div>
            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <Field name='driversLicense' render={field => textInput(field, `Driver's License Number`)} />
                {touched.driversLicense && errors.driversLicense && (
                  <span className='ErrorMsgDivForLastName font-small weight-300 font-red'>{errors.driversLicense}</span>
                )}
              </div>
            </div>

            <div className='flex mt2'>
              <FormLabel>Driver Address</FormLabel>
            </div>

            <div className='bb-shadow-grey'>&nbsp;</div>

            <div className='flex flex-row mt1'>
              <div style={{ flex: 1 }} className='mt1'>
                <Field name='street' render={field => textInput(field, `Street`)} />
                {touched.street && errors.street && (
                  <span className='ErrorMsgDivForLastName font-small weight-300 font-red'>{errors.street}</span>
                )}
              </div>
            </div>

            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <Field name='city' render={field => textInput(field, `City`)} />
                {touched.city && errors.city && <span className='font-small weight-300 font-red'>{errors.city}</span>}
              </div>
              <div style={{ flex: 1 }} className='block ml1 mt1'>
                <Field name='state' render={field => textInput(field, `State`)} />
                {touched.state && errors.state && (
                  <span className='font-small weight-300 font-red'>{errors.state}</span>
                )}
              </div>
            </div>

            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <Field name='zip' render={field => textInput(field, `Zip`)} />
                {touched.zip && errors.zip && <span className='font-small weight-300 font-red'>{errors.zip}</span>}
              </div>
              <div style={{ flex: 1 }} className='block ml1 mt1'>
                <Field name='country' render={field => textInput(field, `Country`)} />
                {touched.country && errors.country && (
                  <span className='font-small weight-300 font-red'>{errors.country}</span>
                )}
              </div>
            </div>
          </div>

          <div className='form-modal-section sub-section'>
            <div className='flex'>
              <FormLabel>Additional Settings</FormLabel>
            </div>

            <div className='bb-shadow-grey'>&nbsp;</div>

            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <FormLabel>Availability For Back-to-Back Trips</FormLabel>
                <Switch checked={availability ? availability : false} onChange={handleToggle} position='start' />
              </div>
            </div>

            <div className='flex flex-row'>
              <div style={{ flex: 1 }} className='mt1'>
                <FormLabel>Dark Mode</FormLabel>
                <Switch checked={darkmode ? darkmode : false} onChange={handleDarkmodeToggle} position='start' />
              </div>
            </div>
          </div>
        </>
      </FloatingForm>
      <EditDriverBottomBar cancelPath={`/drivers/${driver.id}/details`} isSaving={loading} onSave={handleSubmit} />
    </Form>
  )
}

export const PersonalInfoForm = withFormik<IOperatorInfoFormProps, IFormValues>({
  mapPropsToValues: (props: IOperatorInfoFormProps): IFormValues => {
    const {
      email,
      firstName,
      lastName,
      phone,
      countryCode,
      username,
      birthDate,
      driversLicense,
      isAvailableForBackToBack,
      darkmode,
      street,
      city,
      state,
      zip,
      country,
      addressId
    } = props.driver
    return {
      email,
      firstName,
      lastName,
      username,
      phone,
      countryCode,
      birthDate,
      driversLicense,
      isAvailableForBackToBack,
      darkmode,
      street,
      city,
      state,
      zip,
      country,
      addressId
    }
  },
  mapValuesToPayload: x => x,
  validate: ({
    email,
    firstName,
    lastName,
    countryCode,
    phone,
    username,
    driversLicense,
    birthDate
  }: IFormValues): FormikErrors<IFormValues> => {
    const errors: FormikErrors<IFormValues> = {}
    if (!isNotBlank(firstName)) {
      errors.firstName = UIMessages.driver.validation.firstNameIsRequired
    }
    if (!isNotBlank(lastName)) {
      errors.lastName = UIMessages.driver.validation.lastNameIsRequired
    }
    if (!isNotBlank(username)) {
      errors.username = UIMessages.driver.validation.usernameIsRequired
    }
    if (email && !isValidEmail(email)) {
      errors.email = UIMessages.driver.validation.invalidEmailAddress
    }
    if (phone === undefined || !isNotBlank(phone)) {
      errors.phone = UIMessages.driver.validation.phoneNumberIsRequired
    }
    if (driversLicense && !isValidDriversLicense(driversLicense)) {
      errors.driversLicense = UIMessages.driver.validation.driversLicense
    }
    if (birthDate === null || birthDate === undefined || birthDate === '') {
      errors.birthDate = UIMessages.driver.validation.birthDateIsRequired
    }
    if (!isNotBlank(email)) {
      errors.email = UIMessages.driver.validation.emailAddressIsRequired
    }

    return errors
  },
  handleSubmit: (
    {
      email,
      firstName,
      lastName,
      phone,
      countryCode,
      username,
      birthDate,
      driversLicense,
      isAvailableForBackToBack,
      darkmode,
      addressId,
      street,
      state,
      city,
      zip,
      country
    }: IFormValues,
    { props, setSubmitting }
  ) => {
    setSubmitting(false)
    const phoneInfo = phone !== undefined && phone !== '' ? parsePhoneNumber(phone) : ''
    props.handleSubmit({
      id: props.driver.id,
      email,
      firstName,
      lastName,
      username,
      phone: phoneInfo ? phoneInfo.nationalNumber : phone,
      countryCode: phoneInfo ? phoneInfo.countryCallingCode : countryCode,
      birthDate: birthDate ? birthDate : undefined,
      driversLicense,
      isAvailableForBackToBack,
      darkmode,
      addressId,
      street,
      state,
      city,
      zip,
      country
    })
  }
})(InnerForm)

export default PersonalInfoForm



// WEBPACK FOOTER //
// ./src/components/drivers/editdriver/personalinfo/PersonalInfoForm.tsx